/* ==========================================================
   J9 Related Products — assets/j9-related-products.js
   - Loads Shopify recommendations (lazy, when near viewport)
   - Slider: arrows, progress bar, optional auto-slide
   - Quick add to cart (size buttons / single button)
   ========================================================== */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('j9-related-products')) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var toastEl = null;
  var toastTimer = null;

  function showToast(message, ok, cartUrl, viewCartText) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'j9rp-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = '';
    var span = document.createElement('span');
    span.textContent = message;
    toastEl.appendChild(span);
    if (ok && cartUrl) {
      var a = document.createElement('a');
      a.href = cartUrl;
      a.textContent = viewCartText || 'View cart';
      toastEl.appendChild(a);
    }
    toastEl.classList.toggle('is-error', !ok);
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 3500);
  }

  class J9RelatedProducts extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.body = this.querySelector('[data-j9rp-body]');
      if (!this.body) return;
      this._ready = true;

      this._onClick = this.onClick.bind(this);
      this.addEventListener('click', this._onClick);

      var url = this.body.dataset.url;
      if (url) this.lazyLoad(url);
      else this.setup();
    }

    disconnectedCallback() {
      this._ready = false;
      if (this.io) this.io.disconnect();
      this.stopAutoplay();
      if (this._onClick) this.removeEventListener('click', this._onClick);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    /* ---------- Recommendations ---------- */
    lazyLoad(url) {
      if (!('IntersectionObserver' in window)) { this.load(url); return; }
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          this.load(url);
        }
      }, { rootMargin: '0px 0px 600px 0px' });
      this.io.observe(this);
    }

    async load(url) {
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-j9rp-body]');
        if (!fresh || !fresh.querySelector('[data-j9rp-card]')) { this.empty(); return; }
        this.body.innerHTML = fresh.innerHTML;
        this.body.removeAttribute('data-url');
        this.body.classList.remove('is-loading');
        this.setup();
      } catch (err) {
        console.warn('[J9 Related Products]', err);
        this.empty();
      }
    }

    empty() {
      if (window.Shopify && window.Shopify.designMode) {
        this.body.classList.remove('is-loading');
        this.body.innerHTML = '<p class="j9rp__notice">Shopify has no recommendations for this product yet (new products/stores take time). Set a fallback collection, or switch the product source. This message only shows in the theme editor.</p>';
        return;
      }
      var wrap = this.closest('.shopify-section');
      (wrap || this).style.display = 'none';
    }

    /* ---------- Slider ---------- */
    setup() {
      this.track = this.querySelector('[data-j9rp-track]');
      if (!this.track || this.dataset.layout !== 'slider') return;

      this.prevBtns = this.querySelectorAll('[data-j9rp-prev]');
      this.nextBtns = this.querySelectorAll('[data-j9rp-next]');
      this.bar = this.querySelector('[data-j9rp-progress-bar]');

      this.prevBtns.forEach((b) => b.addEventListener('click', () => { this.go(-1); this.restartAutoplay(); }));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => { this.go(1); this.restartAutoplay(); }));

      var raf = 0;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.update());
      }, { passive: true });

      this._onResize = () => this.update();
      window.addEventListener('resize', this._onResize);
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this._onResize, { once: true });
      });

      this.update();
      this.initAutoplay();
    }

    stepSize() {
      var card = this.track.querySelector('[data-j9rp-card]');
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    }

    go(dir) {
      var step = this.stepSize();
      var perView = Math.max(1, Math.floor((this.track.clientWidth + 1) / step));
      this.track.scrollBy({ left: dir * step * perView, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    update() {
      var t = this.track;
      if (!t) return;
      var max = t.scrollWidth - t.clientWidth;
      var overflow = max > 4;
      this.classList.toggle('has-overflow', overflow);

      var atStart = t.scrollLeft <= 4;
      var atEnd = t.scrollLeft >= max - 4;
      this.prevBtns.forEach((b) => { b.disabled = atStart; });
      this.nextBtns.forEach((b) => { b.disabled = atEnd; });

      if (this.bar && t.scrollWidth > 0) {
        this.bar.style.width = (t.clientWidth / t.scrollWidth) * 100 + '%';
        this.bar.style.left = (t.scrollLeft / t.scrollWidth) * 100 + '%';
      }

      var media = t.querySelector('.j9rp__media');
      if (media) this.style.setProperty('--j9rp-arrow-top', Math.max(0, media.offsetHeight / 2 - 20) + 'px');
    }

    initAutoplay() {
      var secs = parseInt(this.dataset.autoplay, 10) || 0;
      if (!secs || reduceMotion) return;
      this.interval = secs * 1000;
      var pause = () => { this.paused = true; };
      var resume = () => { this.paused = false; };
      this.addEventListener('pointerenter', pause);
      this.addEventListener('pointerleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.addEventListener('touchstart', pause, { passive: true });
      this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.interval) return;
      this.timer = setInterval(() => {
        if (this.paused || document.hidden || !this.classList.contains('has-overflow')) return;
        var t = this.track;
        if (t.scrollLeft >= t.scrollWidth - t.clientWidth - 4) t.scrollTo({ left: 0, behavior: 'smooth' });
        else this.go(1);
      }, this.interval);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      if (this.interval) this.startAutoplay();
    }

    /* ---------- Quick add ---------- */
    async onClick(e) {
      var btn = e.target.closest('[data-j9rp-add]');
      if (!btn || !this.contains(btn)) return;
      e.preventDefault();
      if (btn.classList.contains('is-loading')) return;

      var id = Number(btn.dataset.variantId);
      if (!id) return;
      var ds = this.dataset;

      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(ds.cartAddUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

        if (ds.afterAdd === 'cart') { window.location.href = ds.cartUrl; return; }

        var item = data.items ? data.items[0] : data;
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1500);

        this.notifyTheme(item, btn);
        this.refreshCartCount();
        if (ds.toast === 'true') showToast(ds.addedText || 'Added to cart', true, ds.cartUrl, ds.viewCartText);
      } catch (err) {
        showToast(err.message || 'Could not add to cart', false);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }

    notifyTheme(item, btn) {
      // Impulse (Archetype) — refreshes and opens the cart drawer
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item, addToCartBtn: btn } }));
      document.dispatchEvent(new CustomEvent('cart:build'));
      // Generic event used by several other themes / apps
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    async refreshCartCount() {
      try {
        const res = await fetch(this.dataset.cartUrl + '.js', { credentials: 'same-origin' });
        const cart = await res.json();
        var n = cart.item_count;
        document.querySelectorAll('[data-cart-count], .cart-link__bubble-num, .cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = n;
        });
        document.querySelectorAll('.cart-link__bubble').forEach(function (el) {
          el.classList.toggle('cart-link__bubble--visible', n > 0);
        });
      } catch (e) { /* ignore */ }
    }
  }

  customElements.define('j9-related-products', J9RelatedProducts);
})();